import React, { useState, useEffect } from 'react'
import { view } from '@risingstack/react-easy-state'
import styled from 'styled-components'
import { Button, message, Input, Select, Typography } from 'antd'
import { Redirect } from 'react-router-dom'

import globalStore from 'store/index'
import StyledBox from 'components/common/styled-components/StyledBox'

import OrganizationFieldsService from 'services/users/organization-fields.service'
import ApprovalChainsService from 'services/users/approval-chains.service'
import OfferLetterTemplatesService from 'services/users/offer-letter-templates.service'
import LetterTemplatesService from 'services/users/letter-templates.service'
import OrganizationsService from 'services/users/organizations.service'

const { Title } = Typography

const StyledOAuthButton = styled(StyledBox)`
	text-align: center;
	margin-top: 30px;
`

const IntegrationPage = (props) => {
	const user = globalStore.currentUser
	const [organizationId, setOrganizationId] = useState(null)
	const [oAuthToken, setOAuthToken] = useState(null)
	const [syncingOrganizationFields, setSyncingOrganizationFields] = useState(false)
	const [syncingBCSLAOwnerEmail, setSyncingBCSLAOwnerEmail] = useState(false)
	const [bcsla, setBcsla] = useState()

	const [offerLetterTemplates, setOfferLetterTemplates] = useState([])
	const [letterTemplates, setLetterTemplates] = useState([])
	const [selectedOfferLetterTemplates, setSelectedOfferLetterTemplates] = useState([])
	const [selectedLetterTemplates, setSelectedLetterTemplates] = useState([])
	const [updatingOfferLetter, setUpdatingOfferLetter] = useState(false)
	const [updatingAdditionalLetter, setUpdatingAdditionalLetter] = useState(false)

	useEffect(() => {
		if (user) {
			const { organization_id, organization_access_token_response } = user

			setOrganizationId(organization_id)
			setOAuthToken(organization_access_token_response?.access_token)
		}
	}, [user])

	const isWNS = globalStore.currentOrganization?.['is_wns?']

	useEffect(() => {
		if (!isWNS) return
		const fetchData = async () => {
			await Promise.all([fetchOfferLetterTemplates(), fetchLetterTemplates()])
			await fetchLetterAlignmentConfigLists()
		}
		fetchData()
	}, [isWNS]) // eslint-disable-line react-hooks/exhaustive-deps

	const fetchOfferLetterTemplates = async () => {
		const tempState = {}
		await OfferLetterTemplatesService.index({ pageState: tempState, page: 1, pageSize: 100 })
		if (tempState?.offer_letter_templates) {
			setOfferLetterTemplates(tempState.offer_letter_templates)
		}
	}

	const fetchLetterTemplates = async () => {
		const tempState = {}
		await LetterTemplatesService.index({ pageState: tempState, page: 1, pageSize: 100 })
		if (tempState?.letter_templates) {
			setLetterTemplates(tempState.letter_templates)
		}
	}

	const fetchLetterAlignmentConfigLists = async () => {
		const tempState = {}
		await OrganizationsService.letterAlignmentConfigLists({ pageState: tempState })

		const { letter_alignment_config, letterAlignmentConfigErrors } = tempState

		if (letterAlignmentConfigErrors) {
			message.error(letterAlignmentConfigErrors[0] || 'Letter alignment config could not be fetched')
			return
		}

		if (letter_alignment_config?.offer_letter_template_ids) {
			setSelectedOfferLetterTemplates(letter_alignment_config.offer_letter_template_ids)
		}
		if (letter_alignment_config?.additional_letter_template_ids) {
			setSelectedLetterTemplates(letter_alignment_config.additional_letter_template_ids)
		}
	}

	const handleOfferLetterUpdate = async () => {
		const tempState = {}
		try {
			setUpdatingOfferLetter(true)
			await OrganizationsService.updateOfferLetterAlignment({
				pageState: tempState,
				offerLetterTemplateIds: selectedOfferLetterTemplates,
			})

			if (tempState?.serverStatus?.status === 200) {
				const { offer_letter_alignment_ids } = tempState
				if (offer_letter_alignment_ids) {
					setSelectedOfferLetterTemplates(offer_letter_alignment_ids)
				}
				message.success('Offer letter alignment updated successfully')
				return
			}

			const { offerLetterAlignmentIdsErrors } = tempState
			message.error(offerLetterAlignmentIdsErrors?.[0] || 'Offer letter alignment could not be updated')
		} finally {
			setUpdatingOfferLetter(false)
		}
	}

	const handleAdditionalLetterUpdate = async () => {
		const tempState = {}
		try {
			setUpdatingAdditionalLetter(true)
			await OrganizationsService.updateAdditionalLetterAlignment({
				pageState: tempState,
				additionalLetterTemplateIds: selectedLetterTemplates,
			})

			if (tempState?.serverStatus?.status === 200) {
				const { additional_letter_alignment_ids } = tempState
				if (additional_letter_alignment_ids) {
					setSelectedLetterTemplates(additional_letter_alignment_ids)
				}
				message.success('Additional letter alignment updated successfully')
				return
			}

			const { additionalLetterAlignmentIdsErrors } = tempState
			message.error(additionalLetterAlignmentIdsErrors?.[0] || 'Additional letter alignment could not be updated')
		} finally {
			setUpdatingAdditionalLetter(false)
		}
	}

	const connectToOAuth = () => {
		if (!organizationId) return

		const url = `${process.env.REACT_APP_API_BASE_URL}/auth/smart-recruiters/${organizationId}/oauth-login`

		window.location = url
	}

	const syncBCSLAOwnerEmail = async () => {
		const tempState = {}

		setSyncingBCSLAOwnerEmail(true)
		await ApprovalChainsService.syncBCSLAOwnerEmail({
			pageState: tempState,
			bcsla,
		})
		setSyncingBCSLAOwnerEmail(false)

		if (tempState?.serverStatus?.status === 200) {
			message.success(tempState.message)
			return
		}

		message.error(tempState.message || 'Oop!! something went wrong')
	}
	const syncOrganizationFields = async () => {
		const tempState = {}

		setSyncingOrganizationFields(true)
		await OrganizationFieldsService.sync({
			pageState: tempState,
		})
		setSyncingOrganizationFields(false)

		if (tempState?.serverStatus?.status === 200) {
			message.success(tempState.message)
			return
		}

		message.error(tempState.message || 'Oop!! something went wrong')
	}

	const accessibleFeatures = globalStore.currentUser?.accessible_features

	if (accessibleFeatures?.length > 0 && !accessibleFeatures.includes('organization-edit'))
		return <Redirect to={`/`} />

	return (
		<div className='page-content dashboard-page'>
			<StyledOAuthButton>
				<Button type='primary' onClick={syncOrganizationFields} loading={syncingOrganizationFields}>
					Sync Organization Fields
				</Button>
			</StyledOAuthButton>
			{organizationId && (
				<StyledOAuthButton>
					<Button disabled={oAuthToken} onClick={connectToOAuth}>
						{oAuthToken ? 'Already connected' : 'Connect'} to OAuth
					</Button>
				</StyledOAuthButton>
			)}
			<StyledOAuthButton>
				<div style={{ display: 'flex', gap: 10 }}>
					<Input placeholder='Please Enter Bcsla' onChange={(e) => setBcsla(e.target.value)} />
					<Button type='primary' onClick={syncBCSLAOwnerEmail} loading={syncingBCSLAOwnerEmail}>
						Sync BCSLA Owner Email
					</Button>
				</div>
			</StyledOAuthButton>

			{isWNS && (
				<>
					<StyledOAuthButton style={{ textAlign: 'left' }}>
						<Title level={5}>Offer Letter Template Layout</Title>
						<div style={{ display: 'flex', gap: 10, marginTop: 10 }}>
								<Select
									mode='multiple'
									style={{ flex: 1 }}
									placeholder='Select Offer Letter Template(s)'
									value={selectedOfferLetterTemplates}
									onChange={(value) => setSelectedOfferLetterTemplates(value)}
									showSearch
									optionFilterProp='children'
								>
									{offerLetterTemplates.map((template) => (
										<Select.Option key={template.id} value={template.id}>
											{template.name} ({template.id})
										</Select.Option>
									))}
								</Select>
								<Button type='primary' onClick={handleOfferLetterUpdate} loading={updatingOfferLetter}>
									Update
							</Button>
						</div>
					</StyledOAuthButton>

					<StyledOAuthButton style={{ textAlign: 'left' }}>
						<Title level={5}>Additional Letter Template Layout</Title>
						<div style={{ display: 'flex', gap: 10, marginTop: 10 }}>
								<Select
									mode='multiple'
									style={{ flex: 1 }}
									placeholder='Select Additional Letter Template(s)'
									value={selectedLetterTemplates}
									onChange={(value) => setSelectedLetterTemplates(value)}
									showSearch
									optionFilterProp='children'
								>
									{letterTemplates.map((template) => (
										<Select.Option key={template.id} value={template.id}>
											{template.name} ({template.id})
										</Select.Option>
									))}
								</Select>
								<Button type='primary' onClick={handleAdditionalLetterUpdate} loading={updatingAdditionalLetter}>
								Update
								</Button>
						</div>
					</StyledOAuthButton>
				</>
			)}
		</div>
	)
}

export default view(IntegrationPage)
